import React, { useEffect, useState } from "react";
import { Grid, Typography, Container, Button, useTheme } from "@mui/material";
import {
    getCourseCompleteUrl,
    getModuleUrl,
    isLastModuleOfCourse,
} from "@js/util/educationUtil";
import { Box, BadgeImg, RibbonImg, Circle, TextGrid } from "./styled";
import Link from "next/link";
import { EducationModulePageProps } from "@js/components/EducationModule/types";
import { userGlobalContext } from "@js/pages/_app";
import {
    CanClaimBadge,
    ClaimBadgeReponse,
    LocationInfo,
    TrialityApiResponse,
} from "@js/components/Interfaces/interfaces";
import { CustomThemeOptions } from "@js/components/CustomColors/Colors";

const EducationModuleComplete = (props: EducationModulePageProps) => {
    const moduleInfo = props.moduleInfo;
    const moduleContentInfo = moduleInfo.moduleComplete;
    const courseComplete = isLastModuleOfCourse(
        props.courseName,
        props.moduleInfo.id
    );
    const moduleUrl: string = getModuleUrl(
        props.courseName,
        props.moduleInfo.id + 1
    );
    const badgeTokenId = props.badgeTokenId;
    const nextStepsUrl = getCourseCompleteUrl(props.courseName);
    const url = !courseComplete ? moduleUrl : nextStepsUrl;
    const { loggedIn, blockchainNetwork, globalUser } = userGlobalContext();
    const theme: CustomThemeOptions = useTheme();

    const [canClaimBadge, setCanClaimBadge] = useState<boolean>(false);
    const [claimBadgeStatusMessage, setClaimBadgeStatusMessage] = useState<
        string | undefined
    >("");
    const [claimBadgeTxnHash, setClaimBadgeTxnHash] = useState<string>("");
    const [userLocation, setUserLocation] = useState<LocationInfo>({});

    useEffect(() => {
        setClaimBadgeStatusMessage("Checking if badge can be claimed...");
        checkCanClaimBadge();
    }, [globalUser, loggedIn]);

    useEffect(() => {
        async function findLocationInfo() {
            try {
                const res = await fetch("https://apis.cmp.quantcast.com/geoip");
                const locationInfo: LocationInfo = await res.json();
                setUserLocation(locationInfo);
            } catch (e: any) {
                console.error("Failed to fetch location info", e);
            }
        }

        findLocationInfo();
    }, []);

    /**
     * @returns whether this module has a reward badge
     */
    function moduleHasBadge(): boolean {
        return badgeTokenId > 0;
    }

    /**
     * Checks if a user can claim the reward badge
     */
    async function checkCanClaimBadge() {
        if (!moduleHasBadge()) {
            setCanClaimBadge(false);
            setClaimBadgeStatusMessage("");
            return;
        }

        if (!loggedIn) {
            setCanClaimBadge(false);
            setClaimBadgeStatusMessage(
                "Login to claim your Badge Token to show off that you completed this module!"
            );
            return;
        }

        if (!globalUser || !globalUser.address) {
            setCanClaimBadge(false);
            setClaimBadgeStatusMessage(
                "Login to claim your Badge Token to show off that you completed this module!"
            );
            return;
        }

        if (blockchainNetwork !== "starknet") {
            setCanClaimBadge(false);
            setClaimBadgeStatusMessage(
                "To claim your Badge Token, complete the module with a Starknet Wallet"
            );
            return;
        }

        // check with the server to see if the user can claim
        const response = await fetch(
            `/api/canClaimBadge?address=${globalUser.address}&courseName=${props.courseName}&moduleId=${props.moduleInfo.id}`
        );
        const tar: TrialityApiResponse = await response.json();
        if (!tar.success) {
            setCanClaimBadge(false);
            setClaimBadgeStatusMessage(tar.err);
            return;
        }

        const ccb: CanClaimBadge = tar.data;
        setCanClaimBadge(ccb.canClaim);
        if (!ccb.canClaim) {
            setClaimBadgeStatusMessage(ccb.cannotClaimReason);
        } else {
            setClaimBadgeStatusMessage(
                "Congratulations, you are eligible to claim a Badge (ERC1155) to show off that you've completed this module! This operation is free, just click the button below and we'll pay for the gas and mint you a badge!"
            );
        }
    }

    /**
     * Claims a module reward badge
     */
    async function claimBadge() {
        setCanClaimBadge(false);
        setClaimBadgeStatusMessage("Claiming badge... hang tight");
        const courseName = props.courseName;
        const moduleId = props.moduleInfo.id;
        const address = globalUser.address;
        const body = {
            courseName,
            moduleId,
            address,
            userLocation,
        };

        try {
            const response = await fetch(`/api/claimBadge`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });
            const tar: TrialityApiResponse = await response.json();
            const cbr: ClaimBadgeReponse = tar.data;
            if (!tar.success) {
                setClaimBadgeStatusMessage(tar.err);
                // there was an error let them try to claim again
                setCanClaimBadge(true);
                return;
            }

            if (!cbr.claimSuccessful) {
                setClaimBadgeStatusMessage(cbr.claimFailedReason);
                // there was an error let them try to claim again
                setCanClaimBadge(true);
                return;
            }

            setClaimBadgeStatusMessage(
                `Successfully claimed badge! View Transaction:`
            );
            setClaimBadgeTxnHash(cbr.txnHash || "");
        } catch (e: any) {
            setClaimBadgeStatusMessage(e.message);
            setCanClaimBadge(true);
        }
    }

    function getClaimBadgeTxnLink() {
        if (!claimBadgeTxnHash) {
            return <></>;
        }

        const txnUrl = `https://testnet.starkscan.co/tx/${claimBadgeTxnHash}`;
        return (
            <div
                style={{
                    paddingBottom: "10px",
                }}
            >
                <a
                    href={txnUrl}
                    target="_blank"
                    style={{
                        cursor: "pointer",
                        color: theme.palette.hyperlink.main,
                    }}
                >
                    {claimBadgeTxnHash}
                </a>
            </div>
        );
    }

    function getClaimBadgeButton() {
        if (!moduleHasBadge()) {
            return <></>;
        }

        return (
            <Button
                variant="contained"
                sx={{ marginRight: "10px" }}
                disabled={!canClaimBadge}
                onClick={claimBadge}
            >
                Claim Badge!
            </Button>
        );
    }

    return (
        <Container>
            <Grid container sx={{ marginTop: "60px", marginBottom: "60px" }}>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        sx={{
                            margin: "20px",
                            borderColor: moduleContentInfo.imgColor,
                        }}
                    >
                        <RibbonImg src={moduleContentInfo.ribbonImgSrc} />
                        <Circle
                            sx={{ backgroundColor: moduleContentInfo.imgColor }}
                        />
                        <BadgeImg src={moduleContentInfo.badgeImgSrc} />
                        <Grid sx={{ position: "absolute", bottom: "25px" }}>
                            <Typography
                                textAlign={"center"}
                                variant="subtitle1"
                                sx={{ fontWeight: "bold" }}
                            >
                                {moduleContentInfo.badgeName}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                textAlign={"center"}
                            >
                                {moduleContentInfo.badgeCaption}
                            </Typography>
                        </Grid>
                    </Box>
                </Grid>
                <TextGrid item xs={12} sm={12} md={6}>
                    <Typography variant="h5" fontWeight={"bold"}>
                        You have successfully completed module "
                        {moduleInfo.title}"!
                    </Typography>
                    <Typography
                        fontStyle={"italic"}
                        sx={{ margin: "30px 0 0 0" }}
                    >
                        {claimBadgeStatusMessage}
                    </Typography>
                    {getClaimBadgeTxnLink()}
                    <Grid mt={3}>
                        {getClaimBadgeButton()}
                        <Link href={url} passHref>
                            <Button
                                variant="outlined"
                                sx={{ textTransform: "none" }}
                            >
                                {!courseComplete
                                    ? "Proceed to Next Module"
                                    : "Proceed to Next Steps"}
                            </Button>
                        </Link>
                    </Grid>
                </TextGrid>
            </Grid>
        </Container>
    );
};

export default EducationModuleComplete;
